import React from 'react';
import { Loader2 } from 'lucide-react';
import { tokens } from '../design/tokens';
import { focusRing, hitArea } from './utils/a11y';

/**
 * Button Component
 * 
 * A flexible, accessible button component following shadcn/ui patterns.
 * Supports multiple variants, sizes, and states.
 * 
 * @example
 * ```tsx
 * <Button variant="primary">Click me</Button>
 * <Button variant="outline" size="sm">Small</Button>
 * <Button variant="ghost" loading>Loading...</Button>
 * ```
 */

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'outline' | 'ghost' | 'link';
  /** Size of the button */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Show loading spinner */
  loading?: boolean;
  /** Icon to display before text */
  leftIcon?: React.ReactNode;
  /** Icon to display after text */
  rightIcon?: React.ReactNode;
  /** Make button full width */
  fullWidth?: boolean;
  /** Children content */
  children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className = '',
      children,
      type = 'button',
      ...props
    },
    ref
  ) => {
    // Base styles
    const baseStyles = [
      'inline-flex items-center justify-center',
      'font-semibold rounded-xl',
      'transition-all duration-200',
      focusRing,
      hitArea,
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
    ].join(' ');

    // Variant styles (using design token gradients)
    const variantStyles = {
      primary: [
        'bg-gradient-primary',
        'text-white shadow-elev-1 hover:shadow-elev-2',
        'motion-safe:transform motion-safe:hover:-translate-y-0.5',
      ].join(' '),
      
      secondary: [
        'bg-gradient-secondary',
        'text-white shadow-elev-1 hover:shadow-elev-2',
        'motion-safe:transform motion-safe:hover:-translate-y-0.5',
      ].join(' '),
      
      success: [
        'bg-gradient-success',
        'text-white shadow-elev-1 hover:shadow-elev-2',
        'motion-safe:transform motion-safe:hover:-translate-y-0.5',
      ].join(' '),
      
      warning: [
        'bg-gradient-warning',
        'text-white shadow-elev-1 hover:shadow-elev-2',
        'motion-safe:transform motion-safe:hover:-translate-y-0.5',
      ].join(' '),
      
      error: [
        'bg-gradient-error',
        'text-white shadow-elev-1 hover:shadow-elev-2',
        'motion-safe:transform motion-safe:hover:-translate-y-0.5',
      ].join(' '),
      
      outline: [
        'bg-transparent border-2 border-primary-500',
        'text-primary-600 hover:bg-primary-50',
        'dark:text-primary-400 dark:border-primary-400',
        'dark:hover:bg-primary-900/20',
      ].join(' '),
      
      ghost: [
        'bg-transparent text-gray-700',
        'hover:bg-gray-100',
        'dark:text-gray-300 dark:hover:bg-gray-800',
      ].join(' '),
      
      link: [
        'bg-transparent text-primary-600',
        'hover:text-primary-700 hover:underline decoration-2 underline-offset-2',
        'dark:text-primary-400 dark:hover:text-primary-300',
        'shadow-none min-h-0',
      ].join(' '),
    };

    // Size styles (using density tokens for height)
    const sizeStyles = {
      sm: 'h-9 px-3 text-sm gap-1.5',
      md: 'h-ctrl px-4 text-base gap-2',
      lg: 'h-12 px-6 text-lg gap-2.5',
      xl: 'h-14 px-8 text-xl gap-3',
    };

    // Width styles
    const widthStyles = fullWidth ? 'w-full' : '';

    // Combine all styles
    const combinedStyles = [
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      widthStyles,
      className,
    ].join(' ');

    // Loading spinner size based on button size
    const spinnerSize = {
      sm: 14,
      md: 16,
      lg: 18,
      xl: 20,
    };

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={combinedStyles}
        {...props}
      >
        {loading && (
          <Loader2 
            className="animate-spin" 
            size={spinnerSize[size]} 
          />
        )}
        {!loading && leftIcon && leftIcon}
        {children}
        {!loading && rightIcon && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
